import { Button, Dialog, DialogContent, DialogTitle, Tooltip } from "@mui/material";
import Loader from "../../../Components/Loader/Loader";
import DataTable from "../../../Components/DataGrid/DataGrid";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import { systemConfigurationApi } from "../../api/system/system.service";
import { useSearchParams } from "react-router-dom";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { memo, useMemo } from "react";
import toast from "react-hot-toast";
import SystemConfigurationManagementForm, { SystemConfigurationManagementFormValues } from "./SystemConfigurationManagementForm";

const getModalProps = (): { fullWidth?: boolean; maxWidth: "sm" | "md" } => {
    return {
        maxWidth: "md",
        fullWidth: true,
    };
}

const getModalTitle = (modalMode: string | null) => {
    if (modalMode === "create") {
        return "Add New System Configuration";
    }

    if (modalMode === "edit") {
        return "Edit System Configuration";
    }
}

const ActionsColumns = (params: GridRenderCellParams) => {
    const [_, setSearchParams] = useSearchParams();

    function openEditModal() {
        setSearchParams(
            {
                modal: "edit",
                id: params.row.id,
            },
            { replace: true },
        );
    }

    return (
        <div className="flex items-center gap-2">
            <Tooltip placement="bottom" title="Edit" disableInteractive onClick={openEditModal}>
                <EditIcon className="cursor-pointer" />
            </Tooltip>
        </div>
    );
};

const columns: GridColDef[] = [
    {
        field: "actions",
        headerName: "",
        type: "actions",
        renderCell: ActionsColumns,
        hideable: false,
        disableExport: true,
    },
    {
        field: "code",
        headerName: "Code",
        sortable: true,
        minWidth: 110,
        hideable: true,
        flex: 1,
    },
    {
        field: "alias",
        headerName: "Alias",
        sortable: true,
        minWidth: 110,
        hideable: true,
        flex: 1,
    },
];



const { useEditSystemConfigurationMutation, useGetSystemConfigurationQuery, usePostSystemConfigurationMutation } = systemConfigurationApi;
const SystemConfigurationManagement = () => {
    const { data = [], isFetching } = useGetSystemConfigurationQuery();
    const [createRecord] = usePostSystemConfigurationMutation();
    const [editRecord] = useEditSystemConfigurationMutation();
    const [searchParams, setSearchParams] = useSearchParams();

    const modalMode = searchParams.get("modal");
    const id = searchParams.get("id") ?? "";

    const initial_values = useMemo(() => {
        const DEFAULT_INITIAL_VALUE = {
            code: "",
            alias: "",
        };
        if (modalMode !== "edit" && !id) return DEFAULT_INITIAL_VALUE;

        const _system_configuration = data.find((system_configuration) => system_configuration.id === id);

        if (!_system_configuration) return DEFAULT_INITIAL_VALUE;

        return {
            code: _system_configuration.code,
            alias: _system_configuration.alias,
        };
    }, [modalMode, id, data]);

    const handleCloseModal = () => {
        setSearchParams({}, { replace: true }); // Removes all search params
    };

    const openCreateModal = () => {
        setSearchParams({ modal: "create" }, { replace: true });
    };

    async function handleFormSubmit(values: SystemConfigurationManagementFormValues) {
        console.log("values", values);

        const toast_id = toast.loading("Submitting...");
        try {
            if (modalMode === "create") {
                await createRecord({ body: values }).unwrap();
            } else {
                await editRecord({ params: { id }, body: values }).unwrap();
            }
            toast.success("Success", { id: toast_id });
            handleCloseModal();
        } catch (error) {
            console.error("SYSTEM_CONFIGURATION_ERROR", error);
            toast.error("Unable to Submit Request", { id: toast_id });
        }
    }
    const FormComponent = memo(() => {
        if (modalMode === "edit" && isFetching)
            return (
                <div style={{ height: "10rem" }}>
                    <Loader />
                </div>
            );

        return <SystemConfigurationManagementForm defaultValues={initial_values} handleSubmit={handleFormSubmit} />;
    });

    const modal_props = useMemo(() => getModalProps(), []);
    const modal_title = getModalTitle(modalMode);
    return (
        <div style={{ height: "80vh" }}>
            <Button variant="contained" onClick={openCreateModal} style={{ marginBottom: "0.5rem" }}>
                Create
            </Button>
            {(modalMode === "create" || modalMode === "edit" || modalMode === "delete") && (
                <Dialog open={true} onClose={handleCloseModal} {...modal_props}>
                    <DialogTitle className="flex items-center justify-between">
                        {modal_title}
                        <CloseIcon className="assets-info-popup-close-icon pointer" onClick={handleCloseModal} />
                    </DialogTitle>
                    <DialogContent>
                        <FormComponent />
                    </DialogContent>
                </Dialog>
            )}
            <DataTable
                tableInfo={{
                    id: "system-configuration-table",
                    label: "System Configuration",
                    requireAuthSign: false,
                    timestamp: { startTs: null, endTs: null },
                }}
                loading={isFetching}
                rows={data}
                columns={columns}
                showFilterButton={false}
                getRowId={(row) => row.id}
            />
        </div>
    )
};

export default SystemConfigurationManagement;
